import { useRegisterEventFrontApi } from "@shared/api";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import IRegistrationForm from "@shared/entities/Backend/IRegistrationForm";
import AppConst from "@shared/entities/AppConsts";
import { IOrder, ILevel } from "@shared/entities/RegisterEvent";
import { useAuthService } from "./AuthService";
import {
  EnumPaymentType,
  EnumRegistrationUpgradeStatus,
} from "@shared/entities/enum";

import { useNavigate } from "react-router-dom";
import { RouteNames } from "@shared/entities/RouteNames";
import { LocalStorageKey } from "@shared/entities";
/** 處理報名相關的服務 */

interface IRegistrationService {
  /** 更新目前報名狀態 */
  RefreshRegisterStatus: () => Promise<{
    success: boolean;
    level?: ILevel;
    // registration為空時代表未報名
    registration?: IRegistrationForm;
    order: IOrder;
    registrationUpgrade: {
      createdAt?: number;
      registrationUpgradeStatus: EnumRegistrationUpgradeStatus;
    }
    registrationUpgradeOrder: IOrder;
  }>;

  /** 透過paymentType 和 Info前往付款頁 */
  GoToPayPage: (paymentType: EnumPaymentType, paymentInfo: any) => Promise<any>;

  status: {
    level?: ILevel;
    // registration為空時代表未報名
    registration?: IRegistrationForm;
    order: IOrder;
    registrationUpgrade: {
      createdAt?: number;
      registrationUpgradeStatus: EnumRegistrationUpgradeStatus;
    }
    registrationUpgradeOrder: IOrder;
  };

  registration?: IRegistrationForm;
  level?: ILevel;
}

const Context = createContext<IRegistrationService>(null);

export function useRegistrationService() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      "useRegistrationService 必須在RegistrationService之下層執行！"
    );
  }
  return context;
}

export function GetLevelBGColor(level: ILevel, deg: number = 90) {
  if (!level) {
    return;
  }
  return `linear-gradient(${deg}deg,${level.levelAdditionalConfig.colorFrom}, ${level.levelAdditionalConfig.colorTo})`;
}
export const RegistrationService = ({ children }) => {
  const { user } = useAuthService();
  const { GetRegistration } = useRegisterEventFrontApi();
  const navigate = useNavigate();
  const [status, setStatus] = useState<{
    level?: ILevel;
    // registration為空時代表未報名
    registration?: IRegistrationForm;
    order: IOrder;
    registrationUpgrade: {
      createdAt?: number;
      registrationUpgradeStatus: EnumRegistrationUpgradeStatus;
    }
    registrationUpgradeOrder: IOrder;
  }>();

  /** 目前的報名狀態 */
  const registration = useMemo(
    () => status?.registration,
    [status?.registration]
  );

  /** 目前的報名等級資料 */
  const level = useMemo(() => status?.level, [status?.level]);

  /** 手動更新目前已報名狀態 */
  const RefreshRegisterStatus = useCallback(async () => {
    if (!user) {
      setStatus(null);
      return;
    }
    return GetRegistration(AppConst.eventID).then((res) => {
      setStatus(res);
      localStorage.setItem(`${LocalStorageKey.Registration}_${AppConst.eventID}`, JSON.stringify(res.registration))
      return res;
    }).catch(() => {
      setStatus(null);
      localStorage.removeItem(`${LocalStorageKey.Registration}_${AppConst.eventID}`)
      return null
    });
  }, [GetRegistration, user]);

  /** 前往付款頁 */
  const GoToPayPage = useCallback(
    async (paymentType: EnumPaymentType, paymentInfo: any) => {
      switch (paymentType) {
        // 轉跳
        case EnumPaymentType.redirect:
          window.location.href = paymentInfo.url;
          break;
        // 重新整理
        case EnumPaymentType.refresh:
          navigate(`/${RouteNames.Dashboard}/${RouteNames.Dashboard_Register}`);
          await RefreshRegisterStatus();
          break;
        // 寫法 From 松松
        case EnumPaymentType.redirectToECPay:
        case EnumPaymentType.redirectToNewebPay:
          let form = document.createElement("form");
          for (let field of Object.keys(paymentInfo.form)) {
            let input = document.createElement("input");
            input.name = field;
            input.value = paymentInfo.form[field];
            form.appendChild(input);
          }
          form.method = "POST";
          form.action = paymentInfo.url;
          document.body.appendChild(form);
          form.submit();
          break;
      }
    },
    [RefreshRegisterStatus, navigate]
  );

  useEffect(() => {
    // 更新報名狀態
    RefreshRegisterStatus();
  }, [RefreshRegisterStatus]);

  const context: IRegistrationService = useMemo(
    () => ({
      user,
      level,
      RefreshRegisterStatus,
      registration,
      status,
      GoToPayPage,
    }),
    [user, level, RefreshRegisterStatus, registration, status, GoToPayPage]
  );
  return <Context.Provider value={context}>{children}</Context.Provider>;
};
